import { useState } from "react";

import { Button, Callout } from "@blueprintjs/core";
import styled from "@emotion/styled";

import { pluralize } from "@/components/helpers/string-utils";
import { Box, Flex } from "@/components/layout/flexbox";
import { InviteForm } from "@/components/pages_logged_in/workspace-admin/InviteForm";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { SUBTEXT_COLOR } from "@/css/constants";
import { InviteClientsMutation } from "@/graphql";
import { ensureNonNullArray, formatErrors } from "@/utils/array";

type InviteClientsResult = NonNullable<InviteClientsMutation["inviteClients"]>;

interface AlertInformationEmailsProps {
  emails: string[];
  informText: string;
  type: string;
}

function AlertInformationEmails({
  emails,
  informText,
  type,
}: AlertInformationEmailsProps) {
  if (emails.length === 0) return null;

  return (
    <>
      <Box css={{ fontWeight: "bold", padding: "10px 0px" }}>
        {emails.length} {informText}
      </Box>
      <Flex css={{ color: SUBTEXT_COLOR }}>
        <StyledEmailList>
          {emails.map((email) => {
            return <li key={`${type}-${email}`}>{email}</li>;
          })}
        </StyledEmailList>
      </Flex>
    </>
  );
}

interface ResultsProps {
  result: InviteClientsResult;
  onInviteMoreUsers: () => void;
  onClose: () => void;
}

function Results({ result, onInviteMoreUsers, onClose }: ResultsProps) {
  const { ok, errors, toBeInvitedEmails } = result;

  const existedEmails = ensureNonNullArray(result?.existedEmails);
  const invalidEmails = ensureNonNullArray(result?.invalidEmails);

  const errorMessage = formatErrors(errors) || "Something went wrong";

  return (
    <Box px={3}>
      {!ok && <Callout intent="danger">{errorMessage}</Callout>}

      {ok && !toBeInvitedEmails?.length && (
        <Callout intent={"warning"}>0 new users will be invited</Callout>
      )}

      {!!toBeInvitedEmails?.length && (
        <Callout intent="success" icon="tick">
          {`${toBeInvitedEmails?.length} new users will be invited`}
        </Callout>
      )}
      <Box my={12}>
        <AlertInformationEmails
          emails={existedEmails}
          informText="users are already in your workspace:"
          type="existedEmail"
        />
        <AlertInformationEmails
          emails={invalidEmails}
          informText={`invalid ${pluralize({
            amount: invalidEmails.length,
            singular: "email",
            plural: "emails",
            showCount: false,
          })} couldn't be sent:`}
          type="invalidEmails"
        />
      </Box>
      <Box mt={4}>
        <Button
          intent="primary"
          large={true}
          onClick={onInviteMoreUsers}
          loading={false}
          style={{ marginRight: 5 }}
        >
          Invite More Users
        </Button>
        <Button large={true} onClick={() => onClose()} loading={false}>
          Close
        </Button>
      </Box>
    </Box>
  );
}

interface AddUsersDialogProps {
  open: boolean;
  afterAddedMember: () => void;
  workspaceCredit?: string | null;
  onClose: () => void;
}

export function AddUsersDialog({
  open,
  afterAddedMember,
  workspaceCredit,
  onClose,
}: AddUsersDialogProps) {
  const [result, setResult] = useState<InviteClientsResult>();
  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title={<Box fontSize={18}>Add Users</Box>}
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setResult(undefined);
      }}
    >
      <Box
        py={24}
        px={2}
        backgroundColor="#FFF"
        css={{ borderRadius: "0 0 12px 12px" }}
      >
        {result ? (
          <Results
            result={result}
            onInviteMoreUsers={() => setResult(undefined)}
            onClose={onClose}
          />
        ) : (
          <InviteForm
            result={result}
            onResultChange={(newResult) => {
              setResult(newResult);
              if (newResult?.ok) {
                afterAddedMember();
              }
            }}
            inline={true}
            allowSetCredits={true}
            workspaceCredit={workspaceCredit}
          />
        )}
      </Box>
    </StyledDialog>
  );
}

const StyledEmailList = styled("ul")`
  line-height: 160%;
  padding-left: 36px;
`;
